import { isStripeBillingStrategy } from '@ctct/domain-layer';
import { AccountModificationFlowId } from '@ctct/infrastructure-layer';

import { PrepayService } from './services/PrepayService';
import { ChangePaymentPlanPrepayOptionsStrategy } from './strategies/ChangePaymentPlanPrepayOptionsStrategy';
import { PayflowPrepayOptionsStrategy } from './strategies/PayflowPrepayOptionsStrategy';
import type { Init, PrepaySelectUrlAdapter } from './types';
import { managerHelpers } from '../../helpers';
import type { MergedDataReturnType, MergedHandlersReturnType } from '../../types';
import type { WidgetLifecycle } from '../WidgetLifecycle';
import { WithApi } from '../WithApi';

export type PrepaySelectData = MergedDataReturnType<[PrepayService]>;

export type PrepaySelectHandlers = MergedHandlersReturnType<[PrepayService]>;

type TApi = ReturnType<PrepayService['api']>;

export class PrepaySelectManager extends WithApi<TApi> {
  constructor(private urlAdapter: PrepaySelectUrlAdapter) {
    super();
  }

  public prepaySelectInit = ({ pageStore, queries, flags, t, flowId }: Init) => {
    const [paymentPlanQuery, suspensionInfoQuery] = queries;
    const pending = paymentPlanQuery.isPending || suspensionInfoQuery?.isLoading;

    const error = paymentPlanQuery.error || suspensionInfoQuery?.error || null;

    if (pending) {
      return {
        status: 'pending',
        data: null,
        handlers: null,
        lifecycle: {} as WidgetLifecycle,
      } as const;
    }

    if (error) {
      return {
        status: 'error',
        data: error,
        handlers: {
          retry: () => {
            managerHelpers.retryQueries(queries);
          },
        },
        lifecycle: {} as WidgetLifecycle,
      } as const;
    }

    const isStripe = isStripeBillingStrategy(flags.billingStrategy);
    const discountStrategy =
      flowId === AccountModificationFlowId.ChangePaymentPlan
        ? new ChangePaymentPlanPrepayOptionsStrategy({
            t,
            allowSelectionOfShorterTerm: !flags.disallowSelectionOfShorterPaymentPlan,
            isStripe,
          })
        : new PayflowPrepayOptionsStrategy({
            t,
            allowSelectionOfShorterTerm: !flags.disallowSelectionOfShorterPaymentPlan,
            isStripe,
          });

    const prepay = new PrepayService(
      pageStore,
      paymentPlanQuery.data,
      discountStrategy,
      this.urlAdapter,
      flags,
      suspensionInfoQuery?.data
    );

    this.initApi({
      ...prepay.api(),
    });

    return {
      status: 'loaded',
      data: {
        ...prepay.data(),
      },
      handlers: {
        ...prepay.handlers(),
      },
      lifecycle: {
        onMount() {
          prepay.onMount();
        },
      } as WidgetLifecycle,
    } as const;
  };
}
